class VariantPicker extends HTMLElement {
  constructor() {
    super();

    // Bind methods
    this.getVariants = this.getVariants.bind(this);
    this.updateFromRenderApi = this.updateFromRenderApi.bind(this);
    this.updateValues = this.updateValues.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.getSectionsToRender = this.getSectionsToRender.bind(this);
    this.setLoadingState = this.setLoadingState.bind(this);
    this.boundToCartForm = this.boundToCartForm.bind(this);
    this.sellingPlanToggleAddAction = this.sellingPlanToggleAddAction.bind(this);
    this.isLoadingState = this.isLoadingState.bind(this);
    this.handleUpdateValues = this.handleUpdateValues.bind(this);

    // Initialize properties
    this.sectionId = null;
    this.productId = null;
    this.variants = null;
    this.cartForm = null;
    this.shouldUpdateUrl = false;

    this.productUrl = this.dataset.productUrl || '';
    this.notProductTemplate = this.dataset.template != 'product';
  }

  connectedCallback() {

    this.sectionId = this.dataset.sectionId;
    const inputs = this.querySelectorAll(':where(input[js-variant-option], input[js-variant-selling-plan])');
    this.variants = this.getVariants();
    inputs.forEach((input) => {
      input.addEventListener('change', this.handleOptionChange.bind(this));
    });
    if (this.hasAttribute('js-trigger-update-url')) {
      this.shouldUpdateUrl = true;
    }

    //handle bottom sheet picker for mobile devices => bottom sheet change -> change the radio input
    const forms = document.querySelectorAll('form[action="/cart/add"]');
    const form = Array.from(forms).find((f) => f.contains(this));
    const bottomSheet = form.querySelector('[js-bottom-sheet]');
    const bottomSheetInputs = bottomSheet?.querySelectorAll('input[type="radio"]');
    if(bottomSheetInputs) {
      bottomSheetInputs.forEach((input, index) => {
        input.addEventListener('change', (event) => {
          if(inputs[index]) {
            inputs[index].checked = input.checked;
            inputs[index].dispatchEvent(new Event('change'));
          }
        });
      });
    }

    this.sellingPlanToggleAddAction()
  }

  getVariants() {
    try {
      const json = this.querySelector("script[type='application/json']")?.textContent;
      if (!json) return null;
      return JSON.parse(json);
    } catch (error) {
      return null;
    }
  }

  handleOptionChange() {
    const selectedOptions = this.querySelectorAll('input[js-variant-option]:checked');
    const options = Array.from(selectedOptions).map((input) => ({
      name: input.name,
      value: input.value,
    }));
    const variant = this.variants.find((variant) => {
      return options.every((option) => {
        return variant[option.name] === option.value;
      });
    });

    const selectedSellingPlanOption = this.querySelector('input[js-variant-selling-plan]:checked');
    this.handleUpdateValues(variant, selectedSellingPlanOption?.value);

    // Update image variant when not in product page
    if (this.notProductTemplate) {
      this.updateImageLinkVariant(variant);
    }
  }

  updateImageLinkVariant(selectedVariant) {
    
    const srcVariantImage = this.variants.find(variant => variant.id == selectedVariant.id)?.featured_image.src;
    
    if (srcVariantImage) {
      const link = document.querySelector(`#feature-product-link-${this.sectionId}-${this.productId}`);
      const image = document.querySelector(`#feature-product-image-${this.sectionId}-${this.productId}`);
      image.src = srcVariantImage;
      link.href = `${this.productUrl}?variant=${selectedVariant.id}`;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: `price-${this.sectionId}-${this.productId}`,
      },
      {
        id: `floating-variant-picker-image-${this.sectionId}-${this.productId}`,
        outer: true,
      },
      {
        id: `slide-thumbnail-${this.sectionId}-${this.productId}`,
        outer: true,
      },
      {
        id: `variant-option-${this.sectionId}-${this.productId}`,
        outer: true,
      },
      {
        id: `product-slider-${this.sectionId}-${this.productId}`,
        outer: true,
      },
      {
        id: `variant-sold-rate-${this.sectionId}-${this.productId}`,
        outer: true,
      },
      {
        id: `upsell-product-${this.sectionId}-${this.productId}`,
        outer: true,
      }
    ];
  }

  handleUpdateValues(variant, sellingPlan) {
    clearTimeout(this.timeOutUpdateValue);

    this.timeOutUpdateValue = setTimeout(() => {
      this.updateValues(variant, sellingPlan);
    }, 100);
  }

  updateValues(variant, sellingPlan) {
    if (!variant) return;
    this.updateFromRenderApi(variant, sellingPlan);
    if (this.cartForm?.form?.querySelectorAll('[name="id"]')) {
      this.cartForm.form.querySelectorAll('[name="id"]').forEach((input) => {
        input.value = variant.id;
      });
    }
    if (this.shouldUpdateUrl) {
      const url = new URL(window.location.href);
    
      url.searchParams.set('variant', variant.id);
      if (sellingPlan) {
        url.searchParams.set('selling_plan', sellingPlan);
      } else {
        url.searchParams.delete('selling_plan');
      }
      
      // Remove variant part if not in product page
      if (this.notProductTemplate) {
        url = url.split('?')[0];
      }
      window.history.replaceState({}, '', url);
    }
    this.updateAvailability(variant);
  }

  updateAvailability(variant) {
    this.querySelectorAll('[js-variant-add-to-bag]').forEach((button) => {
      button.disabled = !variant.available;
      button.querySelector('div:first-child').innerText = variant.available ? 'Add to bag' : 'Sold out';
    });
  }

  async updateFromRenderApi(variant, sellingPlan) {
    
    try {
      this.setLoadingState(true);
      const url = this.productUrl != '' ? `${this.productUrl}?variant=${variant.id}&selling_plan=${sellingPlan}&section_id=${this.sectionId}` : `${location.pathname}?variant=${variant.id}&selling_plan=${sellingPlan}&section_id=${this.sectionId}`;
      const res = await fetch(
        url
      ).then((res) => res.text());
      const html = new DOMParser().parseFromString(res, 'text/html');
      const sections = this.getSectionsToRender();

      for (const section of sections) {
        const sectionElement = html.getElementById(section.id);
        let targets;
        const scope = section.scope || document;

        if (section.target) {
          targets = Array.from(scope.querySelectorAll(section.target));
        } else {
          targets = scope.getElementById(section.id);
        }

        if (sectionElement && section.trigger) {
          section.trigger();
        }

        if (!Array.isArray(targets)) {
          targets = [targets];
        }
        if (sectionElement && targets.length) {
          for (const target of targets) {
            if (section.outer) {
              target.outerHTML = sectionElement.outerHTML;
            } else {
              target.innerHTML = sectionElement.innerHTML;
            }
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoadingState(false);
      this.sellingPlanToggleAddAction();
    }
  }

  sellingPlanToggleAddAction() {
    const toggleButtonWrapper = this.querySelector('[js-variant-selling-plan-toggle-wrapper]');
    const toggleButton = toggleButtonWrapper?.querySelector('[js-variant-selling-plan-toggle]');
    const radioButtons = toggleButtonWrapper?.querySelectorAll('[js-variant-selling-plan]');
    const subRadios = this.querySelectorAll('.js-variant-selling-plan');

    if(!toggleButton) return;

    // if (subRadios) {
    //   subRadios.forEach(radio => {
    //     if (radio.checked) {
    //       radio.click();
    //     }
    //   });
    // }
    
    toggleButton.addEventListener('change', function() {
        checkSellingPlan(toggleButton.checked)
        if (subRadios) {
          subRadios.forEach(radio => {
            if (!radio.checked) {
              radio.click();
            }
          });
        }
    });
    
    function checkSellingPlan(isToggleChecked) {
        for(let i = 0; i < radioButtons.length; i++) {
            radioButtons[i].checked = radioButtons[i].value == "" ? !isToggleChecked : isToggleChecked;
            radioButtons[i].dispatchEvent(new Event('change'));
        }
    }
  }

  isLoadingState() {
    return this.classList.contains('loading');
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.classList.add('loading');
      document.querySelectorAll('[js-variant-add-to-bag]').forEach((button) => {
        button.disabled = true;
        button.classList.add('loading');
      });
      this.querySelectorAll('input[js-variant-option]').forEach((input) => {
        input.disabled = true;
      });
    } else {
      this.classList.remove('loading');
      document.querySelectorAll('[js-variant-add-to-bag]').forEach((button) => {
        button.disabled = false;
        button.classList.remove('loading');
      });
      this.querySelectorAll('input[js-variant-option]').forEach((input) => {
        input.disabled = false;
      });
    }
  }

  boundToCartForm(form) {
    this.cartForm = form;
    this.productId = this.cartForm.getProductId();
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

if (!customElements.get('variant-picker-modal')) {
  customElements.define(
    'variant-picker-modal',
    class VariantPickerModal extends VariantPicker {
      constructor() {
        super();

        this.boundToCartForm = this.boundToCartForm.bind(this);
        this.updateAvailability = this.updateAvailability.bind(this);
        this.handleOptionChange = this.handleOptionChange.bind(this);

        this.previous_variant = null;
        this.previous_selling_plan = null;

      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        const inputs = this.querySelectorAll(
          ':where(input[js-variant-option-update-trigger], input[js-variant-selling-plan])'
        );
        this.variants = this.getVariants();
        inputs.forEach((input) => {
          input.addEventListener('change', this.handleOptionChange.bind(this));
        });

        this.handleOptionChange();
      }

      handleOptionChange() {
        const selectedOptions = this.querySelectorAll('input[js-variant-option-update-trigger]:checked');
        const options = Array.from(selectedOptions).map((input) => ({
          name: input.name,
          value: input.value,
        }));
        const variant = this.variants.find((variant) => {
          return options.every((option) => {
            return variant[option.name] === option.value;
          });
        });
        
        const selectedSellingPlanOption = this.querySelector('input[js-variant-selling-plan]:checked');
        const selling_plan = variant.selling_plan_allocations.find(
          (s) => s.selling_plan_id.toString() === selectedSellingPlanOption?.value
        );
        this.updateValues(variant, selling_plan);
      }

      updateValues(variant, selling_plan) {
        if (!variant) return;
        
        const selling_plan_id = selling_plan?.selling_plan_id?.toString() || '';
        const variant_id = variant?.id?.toString() || '';

        const priceTags = this.querySelectorAll(`[js-product-price]`);
        for (const tag of priceTags) {
          tag.classList.add('hide');
          if (tag.dataset.sellingPlanId === selling_plan_id && tag.dataset.variantId === variant_id) {
            tag.classList.remove('hide');
          }
        }

        const sellingPlanOptionContainers = this.querySelectorAll('[js-variant-selling-plan-container]');
        for (const sellingPlanTag of sellingPlanOptionContainers) {
          sellingPlanTag.classList.add('hide');
          sellingPlanTag.querySelectorAll('input[js-variant-selling-plan]').forEach((input) => {
            input.checked = false;
          });
          if (sellingPlanTag.dataset.variantId === variant_id) {
            sellingPlanTag.classList.remove('hide');
            sellingPlanTag.querySelectorAll('input[js-variant-selling-plan]').forEach((input) => {
              if (input.value === selling_plan_id) {
                input.checked = true;
              }
            });
          }
        }

        const featuredImages = this.querySelectorAll('[js-featured-image]');
        for (const image of featuredImages) {
          image.classList.add('hide');
          if (image.dataset.variantId === variant_id) {
            image.classList.remove('hide');
          }
        }
        const mainForm = this.cartForm ? this.cartForm.getMainForm() : this;
        if (mainForm) {
          mainForm.querySelectorAll('[name="id"]').forEach((input) => {
            input.value = variant_id;
          })

          const sellingPlanInput = mainForm.querySelector('[name="selling_plan"]');
          if (sellingPlanInput) {
            mainForm.querySelector('[name="selling_plan"]').value = selling_plan_id;
          }
        }

        this.updateAvailability(variant);
      }

      updateAvailability(variant) {
        this.querySelectorAll('[js-variant-add-to-bag]').forEach((button) => {
          button.disabled = !variant.available;
          button.querySelector('div:first-child').innerText = variant.available ? 'Add to bag' : 'Sold out';
        });
      }

      boundToCartForm(form) {
        this.cartForm = form;
        this.productId = this.cartForm.getProductId();
      }
    }
  );
}
